import { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LobbyIcon,
  CoinPackagesIcon,
  SlotsIcon,
  PromotionsIcon,
  HelpCenterIcon,
} from "../components/icons";
import { useClaimIndicators } from "../../contexts/ClaimIndicatorsContext";

/**
 * Mobile Bottom Navigation Component
 * Follows the mobile design guide patterns for touch-friendly navigation
 * Shows the 5 most important navigation items for mobile users
 */
const MobileBottomNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine selected category from current path
  const selectedCategory = useMemo(() => {
    const path = location.pathname;
    if (path === "/" || path === "/lobby") return "lobby";
    if (
      path === "/offers" ||
      path.startsWith("/offers") ||
      path.startsWith("/checkout")
    )
      return "coin-packages";
    if (path.startsWith("/games")) return "slots";
    if (path.startsWith("/promotions")) return "promotions";
    if (path.startsWith("/help")) return "help";
    if (path.startsWith("/redeem")) return null; // don't highlight anything on redeem page
    if (path.startsWith("/vip")) return null; // don't highlight anything on VIP page
    return null; // default: no active item for other routes
  }, [location.pathname]);

  const { showBuyGlow } = useClaimIndicators();

  // Mobile navigation items - 5 key items with Buy in the middle
  // Using the exact same icons as the desktop sidebar
  const mobileNavItems = useMemo(
    () => [
      {
        id: "lobby",
        name: "Lobby",
        icon: (isActive) => (
          <LobbyIcon className="w-6 h-6" isActive={isActive} />
        ),
        href: "/",
        hasNotification: false,
      },
      {
        id: "slots",
        name: "Games",
        icon: (isActive) => (
          <SlotsIcon className="w-6 h-6" isActive={isActive} />
        ),
        href: "/games/slots",
        hasNotification: false,
      },
      {
        id: "coin-packages",
        name: "Buy",
        icon: (isActive) => (
          <CoinPackagesIcon className="w-6 h-6" isActive={isActive} />
        ),
        href: "/offers",
        hasNotification: !!showBuyGlow,
      },
      {
        id: "promotions",
        name: "Promos",
        icon: (isActive) => (
          <PromotionsIcon className="w-6 h-6" isActive={isActive} />
        ),
        href: "/promotions",
        hasNotification: false,
      },
      {
        id: "help",
        name: "Help",
        icon: (isActive) => (
          <HelpCenterIcon className="w-6 h-6" isActive={isActive} />
        ),
        href: "#open-support",
        hasNotification: false,
        external: false,
      },
    ],
    [showBuyGlow],
  );

  // Navigation handler
  const handleNavigation = (href, external = false) => {
    if (href === "#open-support") {
      // The widget registers window.__openSupportChat when it mounts. It may
      // not have mounted yet on a cold load, so retry briefly and leave a flag
      // the widget honours if the tap landed first.
      const tryOpen = (attempt = 0) => {
        if (window.__openSupportChat) {
          window.__openSupportChat();
          return;
        }
        window.__pendingOpenSupport = true;
        if (attempt < 10) {
          setTimeout(() => tryOpen(attempt + 1), attempt < 5 ? 300 : 800);
        }
      };
      tryOpen();
      return;
    }
    if (external) {
      window.open(href, "_blank");
    } else {
      navigate(href);
    }
  };

  return (
    <nav
      id="mobile-bottom-nav"
      className="
      fixed bottom-0 left-0 right-0 z-50
      bg-card border-t border-border
      md:hidden
      safe-area-inset-bottom
    "
    >
      <div className="flex justify-around items-center py-2 px-4">
        {mobileNavItems.map((item) => (
          <MobileNavItem
            key={item.id}
            icon={item.icon}
            label={item.name}
            isActive={selectedCategory === item.id}
            hasNotification={item.hasNotification}
            onClick={() => handleNavigation(item.href)}
          />
        ))}
      </div>
    </nav>
  );
};

/**
 * Individual Mobile Navigation Item
 * Follows touch-friendly design patterns with minimum 44px touch targets
 */
const MobileNavItem = ({ icon, label, isActive, hasNotification, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`
        relative flex flex-col items-center justify-center p-2 min-w-0
        text-xs cursor-pointer transition-colors
        min-h-11 min-w-11
        active:scale-95
        select-none
        transition-all duration-150
        ${isActive ? "text-white" : "text-gray-400 active:text-white"}
      `}
    >
      <div className="relative w-6 h-6 mb-1 flex items-center justify-center">
        {typeof icon === "function" ? icon(isActive) : icon}
        {/* Notification indicator */}
        {hasNotification && (
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
        )}
      </div>
      <span className="truncate max-w-12 font-medium">{label}</span>
    </button>
  );
};

export default MobileBottomNavigation;
